/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment,
  InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Tab, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, ArrowBack, Build, CalendarMonth, CameraAlt, ChevronLeft, ChevronRight,
  Delete, Edit, FileUpload, History, Inventory2, LocalShipping, OpenInNew,
  PictureAsPdf, Remove, WarningAmber,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../api';

// ── Locações ──────────────────────────────────────────────────────────────
const STATUSES_LOC = [
  { value: 'proposta',   label: 'Proposta',   color: 'info' },
  { value: 'aprovado',   label: 'Aprovado',   color: 'warning' },
  { value: 'em_locacao', label: 'Em Locação', color: 'success' },
  { value: 'encerrado',  label: 'Encerrado',  color: 'default' },
  { value: 'cancelado',  label: 'Cancelado',  color: 'error' },
];

const FORMAS_PAG = [
  'PAGAMENTO BOLETO', 'PIX', 'TRANSFERÊNCIA BANCÁRIA',
  'CARTÃO DE CRÉDITO', 'DINHEIRO', 'CHEQUE',
];

const EMPTY_LOC = {
  cliente_id: '', fornecedor: '', obs_itens: '', frete: '', frete_local: 'SÃO PAULO-SP',
  prazo_pagamento: '15 DD', forma_pagamento: 'PAGAMENTO BOLETO', validade_proposta: '10 dias',
  horario_entrega: '', horario_retirada: '', status: 'proposta', observacoes: '', origem: 'aluguel',
};

const EMPTY_ITEM = { qtd: 1, modelo_pta_id: null, maquina_id: null, modelo: '', tag: '', data_inicio: '', data_fim: '', vl_unitario: '', loc_minima: '' };

// ── Máquinas Próprias ─────────────────────────────────────────────────────
const STATUSES_MAQ = [
  { value: 'disponivel', label: 'Disponível', color: 'success' },
  { value: 'em_uso',     label: 'Em Uso',     color: 'warning' },
  { value: 'manutencao', label: 'Manutenção', color: 'error' },
  { value: 'inativo',    label: 'Inativo',    color: 'default' },
];

const EMPTY_MAQ = {
  tag: '', modelo_pta_id: null, modelo: '', descricao: '', status: 'disponivel',
  cliente_id: '', localizacao: '', data_aquisicao: '', observacoes: '',
};

// ── Modelos PTA ───────────────────────────────────────────────────────────
const TIPOS_PTA = [
  'Tesoura', 'Lança Articulada', 'Lança Telescópica',
  'Mastro Vertical', 'Articulada Elétrica', 'Outro',
];

const EMPTY_MOD = {
  codigo: '', nome: '', tipo: '', fabricante: '',
  capacidade_kg: '', altura_max_m: '', largura_m: '', comprimento_m: '',
  descricao: '', observacoes: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────
function calcDias(ini, fim) {
  if (!ini || !fim) return '–';
  const d = Math.ceil((new Date(fim) - new Date(ini)) / 86400000);
  return d > 0 ? `${d} Dia(s)` : '–';
}

function parseMoney(v) {
  if (!v && v !== 0) return 0;
  const n = parseFloat(String(v).replace(/[R$\s.]/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
}

function fmtMoney(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '–';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDate(d) {
  if (!d) return '–';
  try { return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR'); }
  catch { return d; }
}

function fmtDatetime(dt) {
  if (!dt) return '–';
  try { return new Date(dt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return dt; }
}

function modeloLabel(m) {
  if (!m) return '';
  return m.codigo ? `${m.codigo} — ${m.nome}` : m.nome;
}

// ══════════════════════════════════════════════════════════════════════════
export default function Locacoes() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const authH = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const pdfRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // ── ALUGUÉIS ─────────────────────────────────────────────────────────────
  const [locacoes, setLocacoes] = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(true);
  const [locModal, setLocModal] = useState(false);
  const [editLocId, setEditLocId] = useState(null);
  const [locForm, setLocForm] = useState(EMPTY_LOC);
  const [itens, setItens] = useState([{ ...EMPTY_ITEM }]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [savingLoc, setSavingLoc] = useState(false);
  const [filtroStatusLoc, setFiltroStatusLoc] = useState('');
  const [filtroStatusPropria, setFiltroStatusPropria] = useState('');
  const [origemModal, setOrigemModal] = useState('aluguel');
  const [clientes, setClientes] = useState([]);

  // ── MÁQUINAS PRÓPRIAS ────────────────────────────────────────────────────
  const [maquinas, setMaquinas] = useState([]);
  const [loadingMaq, setLoadingMaq] = useState(true);
  const [maqModal, setMaqModal] = useState(false);
  const [editMaqId, setEditMaqId] = useState(null);
  const [maqForm, setMaqForm] = useState(EMPTY_MAQ);
  const [savingMaq, setSavingMaq] = useState(false);
  const [filtroStatusMaq, setFiltroStatusMaq] = useState('');

  // ── MODELOS PTA ───────────────────────────────────────────────────────────
  const [modelos, setModelos] = useState([]);
  const [loadingMod, setLoadingMod] = useState(true);
  const [modModal, setModModal] = useState(false);
  const [editModId, setEditModId] = useState(null);
  const [modForm, setModForm] = useState(EMPTY_MOD);
  const [modFotoFile, setModFotoFile] = useState(null);
  const [modFotoPreview, setModFotoPreview] = useState('');
  const [modFotoExisting, setModFotoExisting] = useState('');
  const [savingMod, setSavingMod] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [histModal, setHistModal] = useState(false);
  const [histModelo, setHistModelo] = useState(null);
  const [fotoPopup, setFotoPopup] = useState('');
  const fotoRef = useRef(null);

  // ── Auto-save rascunho ───────────────────────────────────────────────────
  const [locAutoSaveMsg, setLocAutoSaveMsg] = useState('');
  const [maqAutoSaveMsg, setMaqAutoSaveMsg] = useState('');
  const [modAutoSaveMsg, setModAutoSaveMsg] = useState('');
  const [locCountdown, setLocCountdown] = useState(null);
  const [maqCountdown, setMaqCountdown] = useState(null);
  const [modCountdown, setModCountdown] = useState(null);
  const autoSaveLocRef = useRef(null);
  const autoSaveMaqRef = useRef(null);
  const autoSaveModRef = useRef(null);
  const locCountdownRef = useRef(10);
  const maqCountdownRef = useRef(10);
  const modCountdownRef = useRef(10);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadClientes = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/clientes`, { headers: authH });
      const d = await r.json();
      setClientes(Array.isArray(d) ? d : []);
    } catch {}
  }, [token]); // eslint-disable-line

  const loadModelos = useCallback(async () => {
    setLoadingMod(true);
    try {
      const r = await fetch(`${API_BASE}/modelos_ptas`, { headers: authH });
      const d = await r.json();
      setModelos(Array.isArray(d) ? d : []);
    } catch { showSnack('Erro ao carregar modelos.', 'error'); }
    finally { setLoadingMod(false); }
  }, [token]); // eslint-disable-line

  const loadLocacoes = useCallback(async () => {
    setLoadingLoc(true);
    try {
      const p = new URLSearchParams();
      if (filtroStatusLoc) p.set('status', filtroStatusLoc);
      const r = await fetch(`${API_BASE}/locacoes?${p}`, { headers: authH });
      const d = await r.json();
      setLocacoes(Array.isArray(d) ? d : []);
    } catch { showSnack('Erro ao carregar locações.', 'error'); }
    finally { setLoadingLoc(false); }
  }, [token, filtroStatusLoc]); // eslint-disable-line

  const loadMaquinas = useCallback(async () => {
    setLoadingMaq(true);
    try {
      const p = new URLSearchParams();
      if (filtroStatusMaq) p.set('status', filtroStatusMaq);
      const r = await fetch(`${API_BASE}/maquinas_propria?${p}`, { headers: authH });
      const d = await r.json();
      setMaquinas(Array.isArray(d) ? d : []);
    } catch { showSnack('Erro ao carregar máquinas.', 'error'); }
    finally { setLoadingMaq(false); }
  }, [token, filtroStatusMaq]); // eslint-disable-line

  useEffect(() => { loadClientes(); }, [loadClientes]);
  useEffect(() => { loadModelos(); }, [loadModelos]);
  useEffect(() => { loadLocacoes(); }, [loadLocacoes]);
  useEffect(() => { loadMaquinas(); }, [loadMaquinas]);

  // Auto-save refs keep stable references to avoid stale closures in setInterval
  useEffect(() => { autoSaveLocRef.current = () => handleSaveLoc(true, true); });
  useEffect(() => { autoSaveMaqRef.current = () => handleSaveMaq(true, true); });
  useEffect(() => { autoSaveModRef.current = () => handleSaveMod(true, true); });

  useEffect(() => {
    if (!locModal) { setLocAutoSaveMsg(''); setLocCountdown(null); return; }
    locCountdownRef.current = 10;
    setLocCountdown(10);
    const t = setInterval(() => {
      locCountdownRef.current -= 1;
      if (locCountdownRef.current <= 0) {
        autoSaveLocRef.current?.();
        locCountdownRef.current = 10;
        setLocCountdown(10);
      } else {
        setLocCountdown(locCountdownRef.current);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [locModal]);

  useEffect(() => {
    if (!maqModal) { setMaqAutoSaveMsg(''); setMaqCountdown(null); return; }
    maqCountdownRef.current = 10;
    setMaqCountdown(10);
    const t = setInterval(() => {
      maqCountdownRef.current -= 1;
      if (maqCountdownRef.current <= 0) {
        autoSaveMaqRef.current?.();
        maqCountdownRef.current = 10;
        setMaqCountdown(10);
      } else {
        setMaqCountdown(maqCountdownRef.current);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [maqModal]);

  useEffect(() => {
    if (!modModal) { setModAutoSaveMsg(''); setModCountdown(null); return; }
    modCountdownRef.current = 10;
    setModCountdown(10);
    const t = setInterval(() => {
      modCountdownRef.current -= 1;
      if (modCountdownRef.current <= 0) {
        autoSaveModRef.current?.();
        modCountdownRef.current = 10;
        setModCountdown(10);
      } else {
        setModCountdown(modCountdownRef.current);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [modModal]);

  // ── Cálculos ──────────────────────────────────────────────────────────────
  const totalLocacao  = itens.reduce((s, it) => s + (parseFloat(it.qtd) || 0) * parseMoney(it.vl_unitario), 0);
  const totalProposta = totalLocacao + parseMoney(locForm.frete);

  // ── ALUGUÉIS handlers ─────────────────────────────────────────────────────
  const openNewLoc = () => {
    setEditLocId(null); setLocForm({ ...EMPTY_LOC, origem: 'aluguel' });
    setItens([{ ...EMPTY_ITEM }]); setPdfFile(null); setPdfName('');
    setOrigemModal('aluguel'); setLocModal(true);
  };

  const openNewPropria = () => {
    setEditLocId(null); setLocForm({ ...EMPTY_LOC, origem: 'propria', fornecedor: '' });
    setItens([{ ...EMPTY_ITEM }]); setPdfFile(null); setPdfName('');
    setOrigemModal('propria'); setLocModal(true);
  };

  const openEditLoc = (loc) => {
    setEditLocId(loc.id);
    setOrigemModal(loc.origem || 'aluguel');
    setLocForm({
      cliente_id: String(loc.cliente_id ?? ''), fornecedor: loc.fornecedor || '',
      obs_itens: loc.obs_itens || '', frete: loc.frete ? String(loc.frete) : '',
      frete_local: loc.frete_local || 'SÃO PAULO-SP', prazo_pagamento: loc.prazo_pagamento || '15 DD',
      forma_pagamento: loc.forma_pagamento || 'PAGAMENTO BOLETO', validade_proposta: loc.validade_proposta || '10 dias',
      horario_entrega: loc.horario_entrega ? loc.horario_entrega.slice(0, 16) : '',
      horario_retirada: loc.horario_retirada ? loc.horario_retirada.slice(0, 16) : '',
      status: loc.status || 'proposta', observacoes: loc.observacoes || '',
    });
    setItens(Array.isArray(loc.itens) && loc.itens.length ? loc.itens : [{ ...EMPTY_ITEM }]);
    setPdfFile(null); setPdfName(loc.pdf_filename || '');
    setLocModal(true);
  };

  const handleSaveLoc = async (asRascunho = false, autoSave = false) => {
    if (!asRascunho && !autoSave && itens.some(it => !it.modelo_pta_id)) {
      showSnack('Selecione o modelo PTA em todos os itens.', 'error'); return;
    }
    if (!autoSave) setSavingLoc(true);
    try {
      let pdf_base64 = null, pdf_filename_new = null;
      if (pdfFile && !autoSave) {
        pdf_base64 = await new Promise((res, rej) => {
          const rd = new FileReader();
          rd.onload = e => res(e.target.result.split(',')[1]);
          rd.onerror = rej;
          rd.readAsDataURL(pdfFile);
        });
        pdf_filename_new = pdfFile.name;
      }
      const currentLocId = editLocId;
      const body = {
        ...locForm,
        origem: origemModal,
        rascunho: autoSave ? true : asRascunho,
        cliente_id: locForm.cliente_id ? Number(locForm.cliente_id) : null,
        itens: JSON.stringify(itens),
        valor_total_locacao: totalLocacao, valor_total_proposta: totalProposta,
        ...(pdf_base64 && { pdf_base64, pdf_filename_new }),
      };
      const url = currentLocId ? `${API_BASE}/locacoes/${currentLocId}` : `${API_BASE}/locacoes`;
      const r = await fetch(url, { method: currentLocId ? 'PATCH' : 'POST', headers: authH, body: JSON.stringify({ locacao: body }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.errors?.join(', ') || 'Erro ao salvar.'); }
      const data = await r.json();
      if (autoSave) {
        if (!currentLocId && data.id) setEditLocId(data.id);
        setLocAutoSaveMsg(`Salvo automaticamente às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`);
        loadLocacoes();
        return;
      }
      showSnack(asRascunho ? 'Rascunho salvo.' : currentLocId ? 'Locação atualizada.' : 'Locação criada.');
      setLocModal(false); loadLocacoes();
    } catch (e) { if (!autoSave) showSnack(e.message, 'error'); }
    finally { if (!autoSave) setSavingLoc(false); }
  };

  const handleDeleteLoc = async (loc) => {
    if (!window.confirm(`Excluir locação de "${loc.fornecedor || 'sem fornecedor'}"?`)) return;
    try {
      await fetch(`${API_BASE}/locacoes/${loc.id}`, { method: 'DELETE', headers: authH });
      showSnack('Locação excluída.'); loadLocacoes();
    } catch { showSnack('Erro ao excluir.', 'error'); }
  };

  const openPdf = async (locId) => {
    try {
      const r = await fetch(`${API_BASE}/locacoes/${locId}/show_pdf`, { headers: authH });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { showSnack('Erro ao abrir PDF.', 'error'); }
  };

  const addItem = () => setItens(p => [...p, { ...EMPTY_ITEM }]);
  const remItem = (i) => setItens(p => p.filter((_, idx) => idx !== i));
  const updItemModelo = (i, modeloObj) => setItens(p => p.map((it, idx) =>
    idx === i ? { ...it, modelo_pta_id: modeloObj?.id || null, modelo: modeloObj?.nome || '' } : it
  ));
  const updItemMaquina = (i, maqObj) => setItens(p => p.map((it, idx) =>
    idx === i ? { ...it, maquina_id: maqObj?.id || null, modelo_pta_id: maqObj?.modelo_pta_id || null, tag: maqObj?.tag || '', modelo: maqObj?.modelo || '' } : it
  ));
  const updItem = (i, f, v) => setItens(p => p.map((it, idx) => idx === i ? { ...it, [f]: v } : it));

  // ── MÁQUINAS handlers ─────────────────────────────────────────────────────
  const openNewMaq = () => { setEditMaqId(null); setMaqForm({ ...EMPTY_MAQ }); setMaqModal(true); };
  const openEditMaq = (m) => {
    setEditMaqId(m.id);
    setMaqForm({
      tag: m.tag || '', modelo_pta_id: m.modelo_pta_id || null, modelo: m.modelo || '',
      descricao: m.descricao || '', status: m.status || 'disponivel',
      cliente_id: String(m.cliente_id ?? ''), localizacao: m.localizacao || '',
      data_aquisicao: m.data_aquisicao || '', observacoes: m.observacoes || '',
    });
    setMaqModal(true);
  };

  const handleSaveMaq = async (asRascunho = false, autoSave = false) => {
    if (!asRascunho && !autoSave && !maqForm.modelo_pta_id) { showSnack('Selecione um modelo PTA.', 'error'); return; }
    if (!autoSave) setSavingMaq(true);
    try {
      const currentMaqId = editMaqId;
      const body = { ...maqForm, rascunho: autoSave ? true : asRascunho, cliente_id: maqForm.cliente_id ? Number(maqForm.cliente_id) : null };
      const url = currentMaqId ? `${API_BASE}/maquinas_propria/${currentMaqId}` : `${API_BASE}/maquinas_propria`;
      const r = await fetch(url, { method: currentMaqId ? 'PATCH' : 'POST', headers: authH, body: JSON.stringify({ maquina: body }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.errors?.join(', ') || 'Erro ao salvar.'); }
      const data = await r.json();
      if (autoSave) {
        if (!currentMaqId && data.id) setEditMaqId(data.id);
        setMaqAutoSaveMsg(`Salvo automaticamente às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`);
        loadMaquinas();
        return;
      }
      showSnack(asRascunho ? 'Rascunho salvo.' : currentMaqId ? 'Máquina atualizada.' : 'Máquina cadastrada.');
      setMaqModal(false); loadMaquinas();
    } catch (e) { if (!autoSave) showSnack(e.message, 'error'); }
    finally { if (!autoSave) setSavingMaq(false); }
  };

  const handleDeleteMaq = async (m) => {
    if (!window.confirm(`Excluir máquina "${m.modelo}"?`)) return;
    try {
      await fetch(`${API_BASE}/maquinas_propria/${m.id}`, { method: 'DELETE', headers: authH });
      showSnack('Máquina excluída.'); loadMaquinas();
    } catch { showSnack('Erro ao excluir.', 'error'); }
  };

  // ── MODELOS handlers ──────────────────────────────────────────────────────
  const openNewMod = () => {
    setEditModId(null); setModForm({ ...EMPTY_MOD });
    setModFotoFile(null); setModFotoPreview(''); setModFotoExisting('');
    setModModal(true);
  };
  const openEditMod = (m) => {
    setEditModId(m.id);
    setModForm({
      codigo: m.codigo || '', nome: m.nome || '', tipo: m.tipo || '',
      fabricante: m.fabricante || '', capacidade_kg: m.capacidade_kg || '',
      altura_max_m: m.altura_max_m || '', largura_m: m.largura_m || '',
      comprimento_m: m.comprimento_m || '', descricao: m.descricao || '',
      observacoes: m.observacoes || '',
    });
    setModFotoFile(null);
    setModFotoPreview('');
    setModFotoExisting(m.foto_filename ? `/uploads/modelos_pta/${m.foto_filename}` : '');
    setModModal(true);
  };

  const handleSaveMod = async (asRascunho = false, autoSave = false) => {
    if (!asRascunho && !autoSave && !modForm.nome.trim()) { showSnack('Nome é obrigatório.', 'error'); return; }
    if (!autoSave) setSavingMod(true);
    try {
      let foto_base64 = null, foto_ext = null;
      if (modFotoFile && !autoSave) {
        foto_base64 = await new Promise((res, rej) => {
          const rd = new FileReader();
          rd.onload = e => res(e.target.result.split(',')[1]);
          rd.onerror = rej;
          rd.readAsDataURL(modFotoFile);
        });
        foto_ext = modFotoFile.name.split('.').pop().toLowerCase() || 'jpg';
      }
      const currentModId = editModId;
      const body = { ...modForm, rascunho: autoSave ? true : asRascunho, ...(foto_base64 && { foto_base64, foto_ext }) };
      const url = currentModId ? `${API_BASE}/modelos_ptas/${currentModId}` : `${API_BASE}/modelos_ptas`;
      const r = await fetch(url, { method: currentModId ? 'PATCH' : 'POST', headers: authH, body: JSON.stringify({ modelo: body }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.errors?.join(', ') || 'Erro ao salvar.'); }
      const data = await r.json();
      if (autoSave) {
        if (!currentModId && data.id) setEditModId(data.id);
        setModAutoSaveMsg(`Salvo automaticamente às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`);
        loadModelos();
        return;
      }
      showSnack(asRascunho ? 'Rascunho salvo.' : currentModId ? 'Modelo atualizado.' : 'Modelo cadastrado.');
      setModModal(false); loadModelos();
    } catch (e) { if (!autoSave) showSnack(e.message, 'error'); }
    finally { if (!autoSave) setSavingMod(false); }
  };

  const handleDeleteMod = async (m) => {
    if (!window.confirm(`Excluir modelo "${m.nome}"?`)) return;
    try {
      await fetch(`${API_BASE}/modelos_ptas/${m.id}`, { method: 'DELETE', headers: authH });
      showSnack('Modelo excluído.'); loadModelos();
    } catch { showSnack('Erro ao excluir.', 'error'); }
  };

  const openHist = (m) => { setHistModelo(m); setHistModal(true); };

  // Calcular histórico para o modelo selecionado
  const histLocacoes = histModelo
    ? locacoes.filter(l => Array.isArray(l.itens) && l.itens.some(it => it.modelo_pta_id === histModelo.id))
    : [];
  const histMaquinas = histModelo
    ? maquinas.filter(m => m.modelo_pta_id === histModelo.id)
    : [];

  const modelosFiltrados = filtroTipo ? modelos.filter(m => m.tipo === filtroTipo) : modelos;

  // ── Calendário ────────────────────────────────────────────────────────────
  const [calMes, setCalMes] = useState(() => new Date().getMonth());
  const [calAno, setCalAno] = useState(() => new Date().getFullYear());
  const [calFiltroModelo, setCalFiltroModelo] = useState('');

  const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const DIAS_PT  = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const CAL_CORES_ALUGUEL = ['#1565c0','#6a1b9a','#e65100','#ad1457','#00695c','#4527a0','#0277bd','#558b2f','#d84315','#37474f'];
  const CAL_CORES_PROPRIA = ['#2e7d32','#388e3c','#43a047','#66bb6a','#1b5e20','#00600f','#33691e','#558b2f','#027136','#1a7431'];

  const modeloCor = useCallback((modeloId, origem) => {
    if (origem === 'propria') {
      const idx = modelos.findIndex(m => m.id === modeloId);
      return CAL_CORES_PROPRIA[Math.abs(idx) % CAL_CORES_PROPRIA.length] || '#2e7d32';
    }
    const idx = modelos.findIndex(m => m.id === modeloId);
    return CAL_CORES_ALUGUEL[idx % CAL_CORES_ALUGUEL.length] || '#1565c0';
  }, [modelos]); // eslint-disable-line

  // Retorna locações ativas que usam este modelo nestas datas (conflito)
  const getConflitosItem = useCallback((modeloId, dataIni, dataFim, excludeLocId = null) => {
    if (!modeloId || !dataIni || !dataFim) return [];
    const ini = new Date(dataIni + 'T00:00:00');
    const fim = new Date(dataFim + 'T23:59:59');
    return locacoes.filter(loc => {
      if (excludeLocId && loc.id === excludeLocId) return false;
      if (['cancelado', 'encerrado'].includes(loc.status)) return false;
      return Array.isArray(loc.itens) && loc.itens.some(it => {
        if (it.modelo_pta_id !== modeloId) return false;
        if (!it.data_inicio || !it.data_fim) return false;
        const locIni = new Date(it.data_inicio + 'T00:00:00');
        const locFim = new Date(it.data_fim + 'T23:59:59');
        return ini <= locFim && fim >= locIni;
      });
    });
  }, [locacoes]);

  // Eventos do calendário: { dia, modeloId, modeloNome, clienteNome, locId, status }
  const eventosCalendario = useCallback(() => {
    const primeiroDia = new Date(calAno, calMes, 1);
    const ultimoDia   = new Date(calAno, calMes + 1, 0);
    const eventos = {};
    locacoes.forEach(loc => {
      if (loc.status === 'cancelado') return;
      const clienteObj = clientes.find(c => c.id === loc.cliente_id);
      const clienteNome = clienteObj?.nome || loc.fornecedor || '—';
      (loc.itens || []).forEach(it => {
        if (!it.modelo_pta_id || !it.data_inicio || !it.data_fim) return;
        if (calFiltroModelo && it.modelo_pta_id !== Number(calFiltroModelo)) return;
        const ini = new Date(it.data_inicio + 'T00:00:00');
        const fim = new Date(it.data_fim     + 'T23:59:59');
        for (let d = new Date(Math.max(ini, primeiroDia)); d <= Math.min(fim, ultimoDia); d.setDate(d.getDate() + 1)) {
          const key = d.getDate();
          if (!eventos[key]) eventos[key] = [];
          eventos[key].push({
            modeloId: it.modelo_pta_id,
            modeloNome: it.modelo || modelos.find(m => m.id === it.modelo_pta_id)?.nome || `#${it.modelo_pta_id}`,
            clienteNome,
            locId: loc.id,
            status: loc.status,
            qtd: it.qtd || 1,
            origem: loc.origem || 'aluguel',
            tag: it.tag || '',
          });
        }
      });
    });
    return eventos;
  }, [calMes, calAno, locacoes, clientes, modelos, calFiltroModelo]);

  // Gera as semanas do mês
  const semanasCalendario = useCallback(() => {
    const primeiroDia = new Date(calAno, calMes, 1).getDay();
    const diasNoMes   = new Date(calAno, calMes + 1, 0).getDate();
    const semanas = [];
    let semana = Array(primeiroDia).fill(null);
    for (let d = 1; d <= diasNoMes; d++) {
      semana.push(d);
      if (semana.length === 7) { semanas.push(semana); semana = []; }
    }
    if (semana.length) semanas.push([...semana, ...Array(7 - semana.length).fill(null)]);
    return semanas;
  }, [calMes, calAno]);

  // Divisão por origem
  const locacoesAluguel = locacoes.filter(l => !l.origem || l.origem === 'aluguel');
  const locacoesPropria = locacoes.filter(l => l.origem === 'propria');

  // Contadores
  const cntLoc = {}; STATUSES_LOC.forEach(s => { cntLoc[s.value] = locacoesAluguel.filter(l => l.status === s.value).length; });
  const cntPropria = {}; STATUSES_LOC.forEach(s => { cntPropria[s.value] = locacoesPropria.filter(l => l.status === s.value).length; });
  const cntMaq = {}; STATUSES_MAQ.forEach(s => { cntMaq[s.value] = maquinas.filter(m => m.status === s.value).length; });

  // Filtro de status para própria
  const locacoesPropFiltradas = filtroStatusPropria ? locacoesPropria.filter(l => l.status === filtroStatusPropria) : locacoesPropria;
  const locacoesAlugFiltradas = filtroStatusLoc ? locacoesAluguel.filter(l => l.status === filtroStatusLoc) : locacoesAluguel;

  // Rascunhos unificados
  const rascunhosLoc = locacoes.filter(l => l.rascunho);
  const rascunhosMaq = maquinas.filter(m => m.rascunho);
  const rascunhosMod = modelos.filter(m => m.rascunho);
  const rascunhosTotal = rascunhosLoc.length + rascunhosMaq.length + rascunhosMod.length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/')} size="small"><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>PTA — Controle de Equipamentos</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab icon={<LocalShipping fontSize="small" />} iconPosition="start" label="Aluguéis" />
          <Tab icon={<Build fontSize="small" />} iconPosition="start" label="Próprias" sx={{ color: 'success.main' }} />
          <Tab icon={<Build fontSize="small" />} iconPosition="start" label="Máquinas" />
          <Tab icon={<Inventory2 fontSize="small" />} iconPosition="start" label="Modelos PTA" />
          <Tab icon={<CalendarMonth fontSize="small" />} iconPosition="start" label="Calendário" />
          <Tab icon={<WarningAmber fontSize="small" />} iconPosition="start" label={`Rascunhos${rascunhosTotal ? ` (${rascunhosTotal})` : ''}`} sx={{ color: 'warning.main' }} />
        </Tabs>
      </Box>

      {/* ── TAB 0: ALUGUÉIS ───────────────────────────────────────────────── */}
      {tab === 0 && (() => {
        const locacoes = locacoesAlugFiltradas;
        return (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={openNewLoc} size={isMobile ? 'small' : 'medium'}>Nova Locação</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, overflowX: 'auto', pb: 1 }}>
            {STATUSES_LOC.map(s => (
              <Paper key={s.value} elevation={0} onClick={() => setFiltroStatusLoc(filtroStatusLoc === s.value ? '' : s.value)} sx={{
                px: isMobile ? 1.5 : 2.5, py: isMobile ? 1 : 1.5, borderRadius: 3, cursor: 'pointer', minWidth: isMobile ? 80 : 110, flexShrink: 0,
                border: '2px solid', borderColor: filtroStatusLoc === s.value ? `${s.color}.main` : 'transparent',
                bgcolor: filtroStatusLoc === s.value ? `${s.color}.50` : 'grey.50',
                '&:hover': { bgcolor: `${s.color}.50` }, transition: 'all 0.15s',
              }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, lineHeight: 1 }}>{cntLoc[s.value] || 0}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>{s.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Mobile: cards */}
          {isMobile ? (
            loadingLoc ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            : locacoes.length === 0 ? <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Nenhuma locação encontrada.</Typography>
            : locacoes.map(loc => {
              const st = STATUSES_LOC.find(s => s.value === loc.status);
              const equips = Array.isArray(loc.itens) ? loc.itens.map(i => `${i.qtd}x ${i.modelo}`).join(', ') : '–';
              return (
                <Paper key={loc.id} elevation={0} sx={{ mb: 1.5, p: 2, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{loc.fornecedor || '(sem fornecedor)'}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip size="small" label={st?.label || loc.status} color={st?.color || 'default'} />
                      {loc.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                    </Box>
                  </Box>
                  {loc.cliente_nome && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>👤 {loc.cliente_nome}</Typography>}
                  {equips !== '–' && <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.82rem' }}>🔧 {equips}</Typography>}
                  {(() => { const dt = loc.horario_entrega || (Array.isArray(loc.itens) ? loc.itens.map(i=>i.data_inicio).filter(Boolean).sort()[0] : null); return dt ? <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>📦 Entrega: {loc.horario_entrega ? fmtDatetime(loc.horario_entrega) : fmtDate(dt)}</Typography> : null; })()}
                  {(() => { const dt = loc.horario_retirada || (Array.isArray(loc.itens) ? [...loc.itens.map(i=>i.data_fim).filter(Boolean)].sort().pop() : null); return dt ? <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>📤 Retirada: {loc.horario_retirada ? fmtDatetime(loc.horario_retirada) : fmtDate(dt)}</Typography> : null; })()}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmtMoney(loc.valor_total_proposta)}</Typography>
                    <Box>
                      {loc.pdf_filename && <IconButton size="small" color="error" onClick={() => openPdf(loc.id)}><PictureAsPdf fontSize="small" /></IconButton>}
                      <IconButton size="small" color="primary" onClick={() => openEditLoc(loc)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteLoc(loc)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                </Paper>
              );
            })
          ) : (
            /* Desktop: tabela */
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Fornecedor</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Equipamentos</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Entrega</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Retirada</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingLoc ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : locacoes.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhuma locação encontrada.</TableCell></TableRow>
                  ) : locacoes.map(loc => {
                    const st = STATUSES_LOC.find(s => s.value === loc.status);
                    const equips = Array.isArray(loc.itens) ? loc.itens.map(i => `${i.qtd}x ${i.modelo}`).join(', ') : '–';
                    return (
                      <TableRow key={loc.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{loc.fornecedor || '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{loc.cliente_nome || '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', maxWidth: 200 }}>{equips}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{loc.horario_entrega ? fmtDatetime(loc.horario_entrega) : fmtDate(Array.isArray(loc.itens) ? loc.itens.map(i => i.data_inicio).filter(Boolean).sort()[0] : null)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{loc.horario_retirada ? fmtDatetime(loc.horario_retirada) : fmtDate(Array.isArray(loc.itens) ? [...loc.itens.map(i => i.data_fim).filter(Boolean)].sort().pop() : null)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(loc.valor_total_proposta)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={st?.label || loc.status} color={st?.color || 'default'} />
                            {loc.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {loc.pdf_filename && <Tooltip title="Ver PDF"><IconButton size="small" color="error" onClick={() => openPdf(loc.id)}><PictureAsPdf fontSize="small" /></IconButton></Tooltip>}
                          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => openEditLoc(loc)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleDeleteLoc(loc)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
        );
      })()}

      {/* ── TAB 1: PRÓPRIAS ───────────────────────────────────────────────── */}
      {tab === 1 && (() => {
        const locacoes = locacoesPropFiltradas;
        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>Solicitações de Máquinas Próprias</Typography>
              <Button variant="contained" color="success" startIcon={<Add />} onClick={openNewPropria} size={isMobile ? 'small' : 'medium'}>Nova Solicitação</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, overflowX: 'auto', pb: 1 }}>
              {STATUSES_LOC.map(s => (
                <Paper key={s.value} elevation={0} onClick={() => setFiltroStatusPropria(filtroStatusPropria === s.value ? '' : s.value)} sx={{
                  px: isMobile ? 1.5 : 2.5, py: isMobile ? 1 : 1.5, borderRadius: 3, cursor: 'pointer', minWidth: isMobile ? 80 : 110, flexShrink: 0,
                  border: '2px solid', borderColor: filtroStatusPropria === s.value ? 'success.main' : 'transparent',
                  bgcolor: filtroStatusPropria === s.value ? 'success.50' : 'grey.50',
                  '&:hover': { bgcolor: 'success.50' }, transition: 'all 0.15s',
                }}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, lineHeight: 1 }}>{cntPropria[s.value] || 0}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>{s.label}</Typography>
                </Paper>
              ))}
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '2px solid', borderColor: 'success.light' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Máquinas / Tags</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Equipamentos</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Entrega</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Retirada</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingLoc ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : locacoes.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhuma solicitação própria encontrada.</TableCell></TableRow>
                  ) : locacoes.map(loc => {
                    const st = STATUSES_LOC.find(s => s.value === loc.status);
                    const equips = Array.isArray(loc.itens)
                      ? loc.itens.map(i => `${i.qtd}x ${i.tag ? `[${i.tag}] ` : ''}${i.modelo}`).join(', ')
                      : '–';
                    return (
                      <TableRow key={loc.id} hover sx={{ bgcolor: '#f1f8e9' }}>
                        <TableCell sx={{ fontWeight: 600, color: 'success.dark' }}>
                          {Array.isArray(loc.itens) ? loc.itens.map(i => i.tag || '–').join(', ') : '–'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{loc.cliente_nome || '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', maxWidth: 220 }}>{equips}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{loc.horario_entrega ? fmtDatetime(loc.horario_entrega) : fmtDate(Array.isArray(loc.itens) ? loc.itens.map(i => i.data_inicio).filter(Boolean).sort()[0] : null)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{loc.horario_retirada ? fmtDatetime(loc.horario_retirada) : fmtDate(Array.isArray(loc.itens) ? [...loc.itens.map(i => i.data_fim).filter(Boolean)].sort().pop() : null)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={st?.label || loc.status} color={st?.color || 'default'} />
                            {loc.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar"><IconButton size="small" color="success" onClick={() => openEditLoc(loc)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleDeleteLoc(loc)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}

      {/* ── TAB 2: MÁQUINAS PRÓPRIAS (gestão da frota) ────────────────────── */}
      {tab === 2 && ( // eslint-disable-next-line no-unused-vars
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={openNewMaq} size={isMobile ? 'small' : 'medium'}>Cadastrar Máquina</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, overflowX: 'auto', pb: 1 }}>
            {STATUSES_MAQ.map(s => (
              <Paper key={s.value} elevation={0} onClick={() => setFiltroStatusMaq(filtroStatusMaq === s.value ? '' : s.value)} sx={{
                px: isMobile ? 1.5 : 2.5, py: isMobile ? 1 : 1.5, borderRadius: 3, cursor: 'pointer', minWidth: isMobile ? 90 : 120, flexShrink: 0,
                border: '2px solid', borderColor: filtroStatusMaq === s.value ? `${s.color}.main` : 'transparent',
                bgcolor: filtroStatusMaq === s.value ? `${s.color}.50` : 'grey.50',
                '&:hover': { bgcolor: `${s.color}.50` }, transition: 'all 0.15s',
              }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, lineHeight: 1 }}>{cntMaq[s.value] || 0}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>{s.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Mobile: cards */}
          {isMobile ? (
            loadingMaq ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            : maquinas.length === 0 ? <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Nenhuma máquina cadastrada.</Typography>
            : maquinas.map(m => {
              const st = STATUSES_MAQ.find(s => s.value === m.status);
              return (
                <Paper key={m.id} elevation={0} sx={{ mb: 1.5, p: 2, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.modelo_pta_nome || m.modelo}</Typography>
                      {m.tag && <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{m.tag}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Chip size="small" label={st?.label || m.status} color={st?.color || 'default'} />
                      {m.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                    </Box>
                  </Box>
                  {(m.localizacao || m.cliente_nome) && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>📍 {m.localizacao || m.cliente_nome}</Typography>}
                  {m.descricao && <Typography variant="body2" sx={{ fontSize: '0.82rem', mb: 0.5 }}>{m.descricao}</Typography>}
                  {m.data_aquisicao && <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>📅 Aquisição: {fmtDate(m.data_aquisicao)}</Typography>}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <IconButton size="small" color="primary" onClick={() => openEditMaq(m)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteMaq(m)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Paper>
              );
            })
          ) : (
            /* Desktop: tabela */
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Tag / ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Modelo PTA</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Localização / Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Aquisição</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingMaq ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : maquinas.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhuma máquina cadastrada.</TableCell></TableRow>
                  ) : maquinas.map(m => {
                    const st = STATUSES_MAQ.find(s => s.value === m.status);
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{m.tag || '–'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.modelo_pta_nome || m.modelo}</Typography>
                          {m.modelo_pta_codigo && <Typography variant="caption" color="text.secondary">{m.modelo_pta_codigo}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', maxWidth: 220 }}>{m.descricao || '–'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={st?.label || m.status} color={st?.color || 'default'} />
                            {m.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.localizacao || m.cliente_nome || '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(m.data_aquisicao)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => openEditMaq(m)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleDeleteMaq(m)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── TAB 3: MODELOS PTA ───────────────────────────────────────────── */}
      {tab === 3 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Tipo:</Typography>
              <Chip label="Todos" variant={!filtroTipo ? 'filled' : 'outlined'} color="primary" size="small" onClick={() => setFiltroTipo('')} sx={{ cursor: 'pointer' }} />
              {TIPOS_PTA.map(t => (
                <Chip key={t} label={t} variant={filtroTipo === t ? 'filled' : 'outlined'} size="small" onClick={() => setFiltroTipo(filtroTipo === t ? '' : t)} sx={{ cursor: 'pointer' }} />
              ))}
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openNewMod} size={isMobile ? 'small' : 'medium'}>Novo Modelo</Button>
          </Box>

          {/* Mobile: cards */}
          {isMobile ? (
            loadingMod ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            : modelosFiltrados.length === 0 ? <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Nenhum modelo cadastrado.</Typography>
            : modelosFiltrados.map(m => {
              const nLoc = locacoes.filter(l => Array.isArray(l.itens) && l.itens.some(it => it.modelo_pta_id === m.id)).length;
              const nMaq = maquinas.filter(mq => mq.modelo_pta_id === m.id).length;
              return (
                <Paper key={m.id} elevation={0} sx={{ mb: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}>
                  {/* Foto banner */}
                  {m.foto_filename && (
                    <Box
                      component="img"
                      src={`/uploads/modelos_pta/${m.foto_filename}`}
                      alt={m.nome}
                      sx={{ width: '100%', height: 160, objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
                      onClick={() => setFotoPopup(`/uploads/modelos_pta/${m.foto_filename}`)}
                    />
                  )}
                  <Box sx={{ p: 2 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>{m.nome}</Typography>
                        {m.codigo && <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{m.codigo}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {m.tipo && <Chip size="small" label={m.tipo} variant="outlined" sx={{ flexShrink: 0 }} />}
                        {m.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                      </Box>
                    </Box>
                    {m.fabricante && <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.82rem' }}>🏭 {m.fabricante}</Typography>}

                    {/* Ficha técnica em grid 2x2 */}
                    {(m.altura_max_m || m.capacidade_kg || m.largura_m || m.comprimento_m) && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                        {m.altura_max_m && <Box><Typography variant="caption" color="text.secondary">Alt. trabalho</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.altura_max_m} m</Typography></Box>}
                        {m.capacidade_kg && <Box><Typography variant="caption" color="text.secondary">Capacidade</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.capacidade_kg} kg</Typography></Box>}
                        {m.largura_m && <Box><Typography variant="caption" color="text.secondary">Largura</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.largura_m} m</Typography></Box>}
                        {m.comprimento_m && <Box><Typography variant="caption" color="text.secondary">Comprimento</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.comprimento_m} m</Typography></Box>}
                      </Box>
                    )}

                    {/* Contadores e ações */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip size="small" icon={<LocalShipping sx={{ fontSize: '14px !important' }} />} label={`${nLoc} alug.`} color={nLoc > 0 ? 'info' : 'default'} variant={nLoc > 0 ? 'filled' : 'outlined'} />
                        <Chip size="small" icon={<Build sx={{ fontSize: '14px !important' }} />} label={`${nMaq} próp.`} color={nMaq > 0 ? 'success' : 'default'} variant={nMaq > 0 ? 'filled' : 'outlined'} />
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => openHist(m)} disabled={nLoc === 0 && nMaq === 0}><History fontSize="small" /></IconButton>
                        <IconButton size="small" color="primary" onClick={() => openEditMod(m)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteMod(m)}><Delete fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })
          ) : (
            /* Desktop: tabela */
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700, width: 60 }}>Foto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nome / Modelo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fabricante</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Capacidade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Alt. Trabalho</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Largura</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Comprimento</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Aluguéis</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Próprias</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingMod ? (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : modelosFiltrados.length === 0 ? (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum modelo cadastrado.</TableCell></TableRow>
                  ) : modelosFiltrados.map(m => {
                    const nLoc = locacoes.filter(l => Array.isArray(l.itens) && l.itens.some(it => it.modelo_pta_id === m.id)).length;
                    const nMaq = maquinas.filter(mq => mq.modelo_pta_id === m.id).length;
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ p: '4px 8px' }}>
                          {m.foto_filename ? (
                            <Box component="img" src={`/uploads/modelos_pta/${m.foto_filename}`} alt={m.nome}
                              sx={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }}
                              onClick={() => setFotoPopup(`/uploads/modelos_pta/${m.foto_filename}`)} />
                          ) : (
                            <Box sx={{ width: 52, height: 40, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <CameraAlt sx={{ fontSize: 18, color: 'grey.400' }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{m.codigo || '–'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{m.nome}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {m.tipo && <Chip size="small" label={m.tipo} variant="outlined" />}
                            {m.rascunho && <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.fabricante || '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.capacidade_kg ? `${m.capacidade_kg} kg` : '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.altura_max_m ? `${m.altura_max_m} m` : '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.largura_m ? `${m.largura_m} m` : '–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{m.comprimento_m ? `${m.comprimento_m} m` : '–'}</TableCell>
                        <TableCell align="center"><Chip size="small" label={nLoc} color={nLoc > 0 ? 'info' : 'default'} variant={nLoc > 0 ? 'filled' : 'outlined'} /></TableCell>
                        <TableCell align="center"><Chip size="small" label={nMaq} color={nMaq > 0 ? 'success' : 'default'} variant={nMaq > 0 ? 'filled' : 'outlined'} /></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver histórico"><IconButton size="small" onClick={() => openHist(m)} disabled={nLoc === 0 && nMaq === 0}><History fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => openEditMod(m)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleDeleteMod(m)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── TAB 4: CALENDÁRIO ────────────────────────────────────────────── */}
      {tab === 4 && (() => {
        const eventos = eventosCalendario();
        const semanas = semanasCalendario();
        const hoje = new Date();
        return (
          <Box>
            {/* Header navegação */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => {
                  if (calMes === 0) { setCalMes(11); setCalAno(y => y - 1); } else setCalMes(m => m - 1);
                }}><ChevronLeft /></IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 200, textAlign: 'center' }}>
                  {MESES_PT[calMes]} {calAno}
                </Typography>
                <IconButton size="small" onClick={() => {
                  if (calMes === 11) { setCalMes(0); setCalAno(y => y + 1); } else setCalMes(m => m + 1);
                }}><ChevronRight /></IconButton>
                <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={() => { setCalMes(new Date().getMonth()); setCalAno(new Date().getFullYear()); }}>
                  Hoje
                </Button>
              </Box>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Filtrar modelo</InputLabel>
                <Select value={calFiltroModelo} label="Filtrar modelo" onChange={e => setCalFiltroModelo(e.target.value)}>
                  <MenuItem value="">Todos os modelos</MenuItem>
                  {modelos.map(m => <MenuItem key={m.id} value={m.id}>{m.codigo ? `${m.codigo} — ${m.nome}` : m.nome}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {/* Legenda */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip size="small" label="● Aluguel" sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
              <Chip size="small" label="● Própria" sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
              {modelos.filter(m => !calFiltroModelo || m.id === Number(calFiltroModelo)).map(m => (
                <Chip key={m.id} size="small" label={m.codigo ? `${m.codigo} — ${m.nome}` : m.nome}
                  sx={{ bgcolor: modeloCor(m.id, 'aluguel'), color: '#fff', fontWeight: 600, fontSize: '0.7rem', opacity: 0.85 }} />
              ))}
            </Box>

            {/* Grade do mês */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
              {/* Cabeçalho dias da semana */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: 'grey.100' }}>
                {DIAS_PT.map(d => (
                  <Box key={d} sx={{ py: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', color: d === 'Dom' || d === 'Sáb' ? 'error.main' : 'text.secondary' }}>
                    {d}
                  </Box>
                ))}
              </Box>
              {/* Semanas */}
              {semanas.map((semana, si) => (
                <Box key={si} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: '1px solid', borderColor: 'grey.200' }}>
                  {semana.map((dia, di) => {
                    const isHoje = dia && calAno === hoje.getFullYear() && calMes === hoje.getMonth() && dia === hoje.getDate();
                    const evs = dia ? (eventos[dia] || []) : [];
                    const lotado = evs.length > 0;
                    return (
                      <Box key={di} sx={{
                        minHeight: 90, p: 0.5, borderLeft: di > 0 ? '1px solid' : 'none', borderColor: 'grey.200',
                        bgcolor: !dia ? 'grey.50' : isHoje ? '#e3f2fd' : lotado ? '#fff8e1' : '#fff',
                        verticalAlign: 'top',
                      }}>
                        {dia && (
                          <>
                            <Typography variant="caption" sx={{
                              display: 'block', fontWeight: isHoje ? 800 : 600,
                              color: isHoje ? 'primary.main' : (di === 0 || di === 6) ? 'error.main' : 'text.primary',
                              fontSize: '0.72rem', mb: 0.3,
                            }}>
                              {dia}
                            </Typography>
                            <Stack spacing={0.3}>
                              {evs.slice(0, 3).map((ev, ei) => (
                                <Tooltip key={ei} title={`${ev.modeloNome} • ${ev.clienteNome} • Qtd: ${ev.qtd} • Status: ${ev.status}`} arrow>
                                  <Box sx={{
                                    bgcolor: modeloCor(ev.modeloId, ev.origem), color: '#fff', borderRadius: 0.7,
                                    px: 0.5, py: 0.2, fontSize: '0.62rem', fontWeight: 600,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default',
                                  }}>
                                    {ev.modeloNome.length > 12 ? ev.modeloNome.slice(0, 12) + '…' : ev.modeloNome}
                                  </Box>
                                </Tooltip>
                              ))}
                              {evs.length > 3 && (
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', pl: 0.5 }}>
                                  +{evs.length - 3} mais
                                </Typography>
                              )}
                            </Stack>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Paper>

            {/* Resumo do mês */}
            {Object.keys(eventos).length > 0 && (
              <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Ocupação em {MESES_PT[calMes]}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[...new Map(Object.values(eventos).flat().map(ev => [ev.modeloId, ev])).values()].map(ev => {
                    const diasOcupados = Object.entries(eventos).filter(([, evs]) => evs.some(e => e.modeloId === ev.modeloId)).length;
                    return (
                      <Chip key={ev.modeloId} size="small"
                        label={`${ev.modeloNome}: ${diasOcupados} dia(s)`}
                        sx={{ bgcolor: modeloCor(ev.modeloId, ev.origem), color: '#fff', fontWeight: 600 }} />
                    );
                  })}
                </Box>
              </Paper>
            )}
          </Box>
        );
      })()}

      {/* ── TAB 5: RASCUNHOS ─────────────────────────────────────────────── */}
      {tab === 5 && (
        <Box>
          {rascunhosTotal === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>Nenhum rascunho salvo.</Typography>
          ) : (
            <>
              {/* Locações rascunho */}
              {rascunhosLoc.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping fontSize="small" /> Locações ({rascunhosLoc.length})
                  </Typography>
                  {rascunhosLoc.map(loc => (
                    <Paper key={loc.id} elevation={0} sx={{ mb: 1.5, p: 2, borderRadius: 3, border: '2px solid #ffe082', bgcolor: '#fffde7' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Chip size="small" label={`#${loc.id}`} variant="outlined" />
                            <Chip size="small" label={loc.origem === 'propria' ? 'Própria' : 'Aluguel'} color={loc.origem === 'propria' ? 'success' : 'primary'} />
                            <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />
                          </Box>
                          {loc.fornecedor && <Typography variant="body2" sx={{ fontWeight: 600 }}>{loc.fornecedor}</Typography>}
                          {loc.cliente_nome && <Typography variant="body2" color="text.secondary">Cliente: {loc.cliente_nome}</Typography>}
                          <Typography variant="caption" color="text.secondary">
                            Salvo por: <strong>{loc.criado_por || '—'}</strong> · {fmtDatetime(loc.updated_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => openEditLoc(loc)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteLoc(loc)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Máquinas rascunho */}
              {rascunhosMaq.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build fontSize="small" /> Máquinas Próprias ({rascunhosMaq.length})
                  </Typography>
                  {rascunhosMaq.map(m => (
                    <Paper key={m.id} elevation={0} sx={{ mb: 1.5, p: 2, borderRadius: 3, border: '2px solid #ffe082', bgcolor: '#fffde7' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Chip size="small" label={`#${m.id}`} variant="outlined" />
                            <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />
                          </Box>
                          {m.tag && <Typography variant="body2" sx={{ fontWeight: 600 }}>TAG: {m.tag}</Typography>}
                          {m.modelo && <Typography variant="body2" color="text.secondary">Modelo: {m.modelo}</Typography>}
                          <Typography variant="caption" color="text.secondary">
                            Salvo por: <strong>{m.criado_por || '—'}</strong> · {fmtDatetime(m.updated_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => openEditMaq(m)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteMaq(m)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Modelos rascunho */}
              {rascunhosMod.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Inventory2 fontSize="small" /> Modelos PTA ({rascunhosMod.length})
                  </Typography>
                  {rascunhosMod.map(m => (
                    <Paper key={m.id} elevation={0} sx={{ mb: 1.5, p: 2, borderRadius: 3, border: '2px solid #ffe082', bgcolor: '#fffde7' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Chip size="small" label={`#${m.id}`} variant="outlined" />
                            {m.tipo && <Chip size="small" label={m.tipo} variant="outlined" />}
                            <Chip size="small" label="Rascunho" sx={{ bgcolor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', fontWeight: 700 }} />
                          </Box>
                          {m.nome && <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.codigo ? `${m.codigo} — ${m.nome}` : m.nome}</Typography>}
                          {m.fabricante && <Typography variant="body2" color="text.secondary">Fabricante: {m.fabricante}</Typography>}
                          <Typography variant="caption" color="text.secondary">
                            Salvo por: <strong>{m.criado_por || '—'}</strong> · {fmtDatetime(m.updated_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => openEditMod(m)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteMod(m)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — HISTÓRICO DE USO DO MODELO
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog open={histModal} onClose={() => setHistModal(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Histórico de Uso — {histModelo?.codigo ? `${histModelo.codigo} ` : ''}{histModelo?.nome}
        </DialogTitle>
        <DialogContent dividers>
          {/* Aluguéis */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LocalShipping color="info" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'info.main' }}>
                Aluguéis ({histLocacoes.length})
              </Typography>
            </Box>
            {histLocacoes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>Nenhum aluguel registrado.</Typography>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Fornecedor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Qtd.</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Período</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Entrega</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Retirada</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {histLocacoes.map(loc => {
                      const itemModelo = loc.itens?.find(it => it.modelo_pta_id === histModelo?.id);
                      const st = STATUSES_LOC.find(s => s.value === loc.status);
                      return (
                        <TableRow key={loc.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{loc.fornecedor || '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem' }}>{loc.cliente_nome || '–'}</TableCell>
                          <TableCell>{itemModelo?.qtd || '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {itemModelo?.data_inicio && itemModelo?.data_fim
                              ? `${fmtDate(itemModelo.data_inicio)} → ${fmtDate(itemModelo.data_fim)}`
                              : '–'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDatetime(loc.horario_entrega)}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDatetime(loc.horario_retirada)}</TableCell>
                          <TableCell><Chip size="small" label={st?.label || loc.status} color={st?.color || 'default'} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Máquinas Próprias */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Build color="success" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.main' }}>
                Máquinas Próprias ({histMaquinas.length})
              </Typography>
            </Box>
            {histMaquinas.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>Nenhuma máquina própria cadastrada.</Typography>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Tag / ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Localização / Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Aquisição</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {histMaquinas.map(m => {
                      const st = STATUSES_MAQ.find(s => s.value === m.status);
                      return (
                        <TableRow key={m.id} hover>
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{m.tag || '–'}</TableCell>
                          <TableCell><Chip size="small" label={st?.label || m.status} color={st?.color || 'default'} /></TableCell>
                          <TableCell sx={{ fontSize: '0.82rem' }}>{m.localizacao || m.cliente_nome || '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(m.data_aquisicao)}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem' }}>{m.descricao || '–'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistModal(false)} variant="contained">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — NOVO / EDITAR MODELO PTA
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog open={modModal} onClose={() => setModModal(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editModId ? 'Editar Modelo PTA' : 'Novo Modelo PTA'}</DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Foto */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>Foto do Equipamento</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    width: 320, height: 240, borderRadius: 2, border: '2px dashed',
                    borderColor: 'grey.300', bgcolor: 'grey.50', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer',
                  }}
                  onClick={() => fotoRef.current?.click()}
                >
                  {modFotoPreview ? (
                    <Box component="img" src={modFotoPreview} alt="preview" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : modFotoExisting ? (
                    <Box component="img" src={modFotoExisting} alt="foto" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Stack alignItems="center" spacing={0.5}>
                      <CameraAlt sx={{ fontSize: 36, color: 'grey.400' }} />
                      <Typography variant="caption" color="text.secondary">Clique para adicionar</Typography>
                    </Stack>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setModFotoFile(f);
                        setModFotoPreview(URL.createObjectURL(f));
                      }
                    }}
                  />
                  <Button variant="outlined" startIcon={<FileUpload />} onClick={() => fotoRef.current?.click()} size="small">
                    {modFotoPreview || modFotoExisting ? 'Trocar Foto' : 'Selecionar Foto'}
                  </Button>
                  {(modFotoPreview || modFotoExisting) && (
                    <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => { setModFotoFile(null); setModFotoPreview(''); setModFotoExisting(''); }}>
                      Remover
                    </Button>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    JPG, PNG, WEBP — máx. 5 MB
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Identificação */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>Identificação</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField key={`cod-${editModId ?? 'new'}`} label="Código / Referência" defaultValue={modForm.codigo}
                  onBlur={e => setModForm(f => ({ ...f, codigo: e.target.value }))} sx={{ width: 160 }} placeholder="Ex: GS3246" />
                <TextField key={`nom-${editModId ?? 'new'}`} label="Nome *" defaultValue={modForm.nome}
                  onBlur={e => setModForm(f => ({ ...f, nome: e.target.value }))} sx={{ flex: 1, minWidth: 200 }} placeholder="Ex: Tesoura 12m Genie GS3246 E-DRIVE" />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <FormControl sx={{ flex: 1, minWidth: 180 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={modForm.tipo} label="Tipo" onChange={e => setModForm(f => ({ ...f, tipo: e.target.value }))}>
                    <MenuItem value="">Selecione</MenuItem>
                    {TIPOS_PTA.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField key={`fab-${editModId ?? 'new'}`} label="Fabricante" defaultValue={modForm.fabricante}
                  onBlur={e => setModForm(f => ({ ...f, fabricante: e.target.value }))} sx={{ flex: 1, minWidth: 160 }} placeholder="Ex: Genie, JLG, Merlo" />
              </Box>
            </Box>

            <Divider />

            {/* Ficha Técnica */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>Ficha Técnica</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField key={`alt-${editModId ?? 'new'}`} label="Altura de trabalho máxima" defaultValue={modForm.altura_max_m}
                  onBlur={e => setModForm(f => ({ ...f, altura_max_m: e.target.value }))} sx={{ flex: 1, minWidth: 160 }}
                  placeholder="Ex: 11,57" InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }} />
                <TextField key={`cap-${editModId ?? 'new'}`} label="Capacidade da plataforma" defaultValue={modForm.capacidade_kg}
                  onBlur={e => setModForm(f => ({ ...f, capacidade_kg: e.target.value }))} sx={{ flex: 1, minWidth: 160 }}
                  placeholder="Ex: 318" InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} />
                <TextField key={`larg-${editModId ?? 'new'}`} label="Largura do equipamento" defaultValue={modForm.largura_m}
                  onBlur={e => setModForm(f => ({ ...f, largura_m: e.target.value }))} sx={{ flex: 1, minWidth: 160 }}
                  placeholder="Ex: 1,16" InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }} />
                <TextField key={`comp-${editModId ?? 'new'}`} label="Comprimento do equipamento" defaultValue={modForm.comprimento_m}
                  onBlur={e => setModForm(f => ({ ...f, comprimento_m: e.target.value }))} sx={{ flex: 1, minWidth: 160 }}
                  placeholder="Ex: 2,44" InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }} />
              </Box>
            </Box>

            <Divider />

            {/* Descrição */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>Descrição</Typography>
              <TextField key={`desc-${editModId ?? 'new'}`} label="Descrição do equipamento" defaultValue={modForm.descricao}
                onBlur={e => setModForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={4}
                placeholder="Descreva as características e diferenciais do equipamento..." />
            </Box>

            <TextField key={`obsmod-${editModId ?? 'new'}`} label="Observações internas" defaultValue={modForm.observacoes}
              onBlur={e => setModForm(f => ({ ...f, observacoes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 0.5 }}>
          {modCountdown !== null && (
            <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>
              {modAutoSaveMsg ? `${modAutoSaveMsg} · Próximo em ${modCountdown}s` : `Salvando em ${modCountdown}s...`}
            </Typography>
          )}
          <Button onClick={() => setModModal(false)}>Cancelar</Button>
          <Button variant="outlined" color="warning" onClick={() => handleSaveMod(true)} disabled={savingMod}>
            Salvar Rascunho
          </Button>
          <Button variant="contained" onClick={() => handleSaveMod(false)} disabled={savingMod} size="large">
            {savingMod ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — NOVA / EDITAR LOCAÇÃO
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog open={locModal} onClose={() => setLocModal(false)} fullWidth maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: isMobile ? '1rem' : undefined }}>
          {editLocId ? 'Editar Locação' : 'Nova Locação'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
          <Stack spacing={isMobile ? 2 : 3}>

            {/* Dados Gerais */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Dados Gerais</Typography>
              <Stack spacing={1.5}>
                {origemModal !== 'propria' && (
                  <TextField key={`forn-${editLocId ?? 'new'}`} label="Fornecedor" defaultValue={locForm.fornecedor}
                    onBlur={e => setLocForm(f => ({ ...f, fornecedor: e.target.value }))} fullWidth size={isMobile ? 'small' : 'medium'} />
                )}
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Cliente</InputLabel>
                  <Select value={locForm.cliente_id} label="Cliente" onChange={e => setLocForm(f => ({ ...f, cliente_id: e.target.value }))}>
                    <MenuItem value="">Sem cliente</MenuItem>
                    {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ maxWidth: isMobile ? undefined : 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={locForm.status} label="Status" onChange={e => setLocForm(f => ({ ...f, status: e.target.value }))}>
                    {STATUSES_LOC.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Itens */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>Itens da Locação</Typography>
                <Button size="small" startIcon={<Add />} onClick={addItem}>Adicionar</Button>
              </Box>
              {modelos.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>Nenhum modelo PTA cadastrado.</Alert>
              )}

              {isMobile ? (
                /* Mobile: cards por item */
                <Stack spacing={1.5}>
                  {itens.map((it, i) => (
                    <Paper key={i} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: (origemModal === 'propria' ? it.maquina_id : it.modelo_pta_id) ? 'grey.200' : 'error.200', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>Item {i + 1}</Typography>
                        {itens.length > 1 && <IconButton size="small" color="error" onClick={() => remItem(i)}><Remove fontSize="small" /></IconButton>}
                      </Box>
                      <Stack spacing={1}>
                        {origemModal === 'propria' ? (
                          <Autocomplete size="small" options={maquinas}
                            getOptionLabel={m => `[${m.tag}] ${m.modelo || ''}`}
                            value={maquinas.find(m => m.id === it.maquina_id) || null}
                            onChange={(_, v) => updItemMaquina(i, v)} isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => (
                              <TextField {...params} label="Máquina *" size="small"
                                error={!it.maquina_id} helperText={!it.maquina_id ? 'Obrigatório' : ''} />
                            )} />
                        ) : (
                          <Autocomplete
                            size="small"
                            options={modelos}
                            getOptionLabel={m => modeloLabel(m)}
                            value={modelos.find(m => m.id === it.modelo_pta_id) || null}
                            onChange={(_, v) => updItemModelo(i, v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => (
                              <TextField {...params} label="Modelo PTA *" size="small"
                                error={!it.modelo_pta_id} helperText={!it.modelo_pta_id ? 'Obrigatório' : ''} />
                            )}
                          />
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField size="small" label="Qtd." type="number" value={it.qtd}
                            onChange={e => updItem(i, 'qtd', e.target.value)}
                            inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ width: 70 }} />
                          <TextField size="small" label="Loc. Mínima" value={it.loc_minima}
                            onChange={e => updItem(i, 'loc_minima', e.target.value)}
                            placeholder="Ex: 1 mês" sx={{ flex: 1 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField size="small" label="Início" type="date" value={it.data_inicio}
                            onChange={e => updItem(i, 'data_inicio', e.target.value)}
                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                          <TextField size="small" label="Fim" type="date" value={it.data_fim}
                            onChange={e => updItem(i, 'data_fim', e.target.value)}
                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField size="small" label="Vl. Unitário" value={it.vl_unitario}
                            onChange={e => updItem(i, 'vl_unitario', e.target.value)}
                            placeholder="1.950,00"
                            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                            sx={{ flex: 1 }} />
                          <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                            <Typography variant="caption" color="text.secondary">Total</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {fmtMoney((parseFloat(it.qtd) || 0) * parseMoney(it.vl_unitario))}
                            </Typography>
                            {(it.data_inicio && it.data_fim) && (
                              <Typography variant="caption" color="text.secondary">{calcDias(it.data_inicio, it.data_fim)}</Typography>
                            )}
                          </Box>
                        </Box>
                        {/* Alerta de conflito */}
                        {(() => {
                          const conf = getConflitosItem(it.modelo_pta_id, it.data_inicio, it.data_fim, editLocId);
                          if (!conf.length) return null;
                          return (
                            <Alert severity="error" icon={<WarningAmber fontSize="small" />} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                              <strong>Conflito!</strong> Este modelo já está locado neste período em {conf.length} locação(ões).
                            </Alert>
                          );
                        })()}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                /* Desktop: tabela */
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700, width: 70 }}>Qtd.</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>{origemModal === 'propria' ? 'Máquina *' : 'Modelo PTA *'}</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 140 }}>Data Início</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 140 }}>Data Fim</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 110 }}>Período</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 150 }}>Vl. Unit.</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 130 }}>Vl. Total</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Loc. Mín.</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens.map((it, i) => (
                        <React.Fragment key={i}>
                        <TableRow>
                          <TableCell>
                            <TextField size="small" type="number" value={it.qtd}
                              onChange={e => updItem(i, 'qtd', e.target.value)}
                              inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ width: 65 }} />
                          </TableCell>
                          <TableCell>
                            {origemModal === 'propria' ? (
                              <Autocomplete size="small" options={maquinas}
                                getOptionLabel={m => `[${m.tag}] ${m.modelo || ''}`}
                                value={maquinas.find(m => m.id === it.maquina_id) || null}
                                onChange={(_, v) => updItemMaquina(i, v)} isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Selecione a máquina..." size="small"
                                    error={!it.maquina_id} helperText={!it.maquina_id ? 'Obrigatório' : ''} />
                                )} sx={{ minWidth: 210 }} />
                            ) : (
                              <Autocomplete size="small" options={modelos} getOptionLabel={m => modeloLabel(m)}
                                value={modelos.find(m => m.id === it.modelo_pta_id) || null}
                                onChange={(_, v) => updItemModelo(i, v)} isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Selecione o modelo..." size="small"
                                    error={!it.modelo_pta_id} helperText={!it.modelo_pta_id ? 'Obrigatório' : ''} />
                                )} sx={{ minWidth: 210 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="date" value={it.data_inicio}
                              onChange={e => updItem(i, 'data_inicio', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 135 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="date" value={it.data_fim}
                              onChange={e => updItem(i, 'data_fim', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 135 }} />
                          </TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{calcDias(it.data_inicio, it.data_fim)}</Typography></TableCell>
                          <TableCell>
                            <TextField size="small" value={it.vl_unitario} onChange={e => updItem(i, 'vl_unitario', e.target.value)}
                              placeholder="1.950,00" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} sx={{ width: 140 }} />
                          </TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{fmtMoney((parseFloat(it.qtd) || 0) * parseMoney(it.vl_unitario))}</Typography></TableCell>
                          <TableCell>
                            <TextField size="small" value={it.loc_minima} onChange={e => updItem(i, 'loc_minima', e.target.value)} placeholder="Ex: 1 mês" sx={{ width: 110 }} />
                          </TableCell>
                          <TableCell>{itens.length > 1 && <IconButton size="small" color="error" onClick={() => remItem(i)}><Remove fontSize="small" /></IconButton>}</TableCell>
                        </TableRow>
                        {/* Linha de conflito */}
                        {(() => {
                          const conf = getConflitosItem(it.modelo_pta_id, it.data_inicio, it.data_fim, editLocId);
                          if (!conf.length) return null;
                          return (
                            <TableRow sx={{ bgcolor: '#fff3f3' }}>
                              <TableCell colSpan={9} sx={{ py: 0.5 }}>
                                <Alert severity="error" icon={<WarningAmber fontSize="small" />} sx={{ py: 0.2, fontSize: '0.75rem' }}>
                                  <strong>Conflito!</strong> Este modelo já está locado neste período em {conf.length} locação(ões). Verifique o Calendário.
                                </Alert>
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TextField key={`obs-it-${editLocId ?? 'new'}`} label="Observações dos Itens" defaultValue={locForm.obs_itens}
                onBlur={e => setLocForm(f => ({ ...f, obs_itens: e.target.value }))}
                fullWidth multiline rows={2} sx={{ mt: 1.5 }} size={isMobile ? 'small' : 'medium'}
                placeholder="Ex: MODELO E MARCA OFERTADO PODEM VARIAR DE ACORDO COM A DISPONIBILIDADE" />
            </Box>

            <Divider />

            {/* Valores */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Valores</Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', flex: 1, minWidth: 130 }}>
                    <Typography variant="caption" color="text.secondary">Total da Locação</Typography>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700 }}>{fmtMoney(totalLocacao)}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100', flex: 1, minWidth: 130 }}>
                    <Typography variant="caption" color="text.secondary">Total da Proposta</Typography>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, color: 'primary.main' }}>{fmtMoney(totalProposta)}</Typography>
                  </Paper>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField key={`frete-${editLocId ?? 'new'}`} label="Frete — Entrega e Retira" defaultValue={locForm.frete}
                    onBlur={e => setLocForm(f => ({ ...f, frete: e.target.value }))} size={isMobile ? 'small' : 'medium'}
                    sx={{ flex: 1, minWidth: 140 }} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                  <TextField key={`frete-local-${editLocId ?? 'new'}`} label="Cidade / UF" defaultValue={locForm.frete_local}
                    onBlur={e => setLocForm(f => ({ ...f, frete_local: e.target.value }))} size={isMobile ? 'small' : 'medium'}
                    sx={{ flex: 1, minWidth: 120 }} placeholder="SÃO PAULO-SP" />
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Condições e Prazos */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Condições e Prazos</Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField key={`pz-${editLocId ?? 'new'}`} label="Prazo para Pagamento" defaultValue={locForm.prazo_pagamento}
                    onBlur={e => setLocForm(f => ({ ...f, prazo_pagamento: e.target.value }))}
                    size={isMobile ? 'small' : 'medium'} sx={{ flex: 1, minWidth: 130 }} placeholder="Ex: 15 DD" />
                  <TextField key={`val-${editLocId ?? 'new'}`} label="Validade da Proposta" defaultValue={locForm.validade_proposta}
                    onBlur={e => setLocForm(f => ({ ...f, validade_proposta: e.target.value }))}
                    size={isMobile ? 'small' : 'medium'} sx={{ flex: 1, minWidth: 130 }} placeholder="Ex: 10 dias" />
                </Box>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Forma de Pagamento</InputLabel>
                  <Select value={locForm.forma_pagamento} label="Forma de Pagamento" onChange={e => setLocForm(f => ({ ...f, forma_pagamento: e.target.value }))}>
                    {FORMAS_PAG.map(fp => <MenuItem key={fp} value={fp}>{fp}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Horários */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth fontSize="small" /> Horários de Entrega e Retirada
              </Typography>
              <Stack spacing={1.5}>
                <TextField label="Horário de Entrega" type="datetime-local" value={locForm.horario_entrega}
                  onChange={e => setLocForm(f => ({ ...f, horario_entrega: e.target.value }))}
                  fullWidth size={isMobile ? 'small' : 'medium'} InputLabelProps={{ shrink: true }} />
                <TextField label="Horário de Retirada" type="datetime-local" value={locForm.horario_retirada}
                  onChange={e => setLocForm(f => ({ ...f, horario_retirada: e.target.value }))}
                  fullWidth size={isMobile ? 'small' : 'medium'} InputLabelProps={{ shrink: true }} />
              </Stack>
            </Box>

            <Divider />

            {/* PDF */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Proposta em PDF</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <input ref={pdfRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { setPdfFile(f); setPdfName(f.name); } }} />
                <Button variant="outlined" startIcon={<FileUpload />} onClick={() => pdfRef.current?.click()} size={isMobile ? 'small' : 'medium'}>
                  {pdfName ? 'Trocar PDF' : 'Anexar PDF'}
                </Button>
                {pdfName && (
                  <Chip icon={<PictureAsPdf />} label={pdfName.length > 30 ? pdfName.slice(0, 30) + '…' : pdfName}
                    color="error" variant="outlined" size="small" onDelete={() => { setPdfFile(null); setPdfName(''); }} />
                )}
                {editLocId && pdfName && !pdfFile && (
                  <IconButton size="small" color="error" onClick={() => openPdf(editLocId)}><OpenInNew fontSize="small" /></IconButton>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Observações */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Observações</Typography>
              <TextField key={`obs-${editLocId ?? 'new'}`} label="Observações Gerais" defaultValue={locForm.observacoes}
                onBlur={e => setLocForm(f => ({ ...f, observacoes: e.target.value }))}
                fullWidth multiline rows={3} size={isMobile ? 'small' : 'medium'} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 1.5 : 2, flexWrap: 'wrap', gap: 1 }}>
          {locCountdown !== null && (
            <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>
              {locAutoSaveMsg ? `${locAutoSaveMsg} · Próximo em ${locCountdown}s` : `Salvando em ${locCountdown}s...`}
            </Typography>
          )}
          <Button onClick={() => setLocModal(false)} size={isMobile ? 'small' : 'medium'}>Cancelar</Button>
          <Button variant="outlined" color="warning" onClick={() => handleSaveLoc(true)} disabled={savingLoc} size={isMobile ? 'small' : 'medium'}>
            Salvar Rascunho
          </Button>
          <Button variant="contained" onClick={() => handleSaveLoc(false)} disabled={savingLoc} size={isMobile ? 'medium' : 'large'} sx={{ minWidth: 100 }}>
            {savingLoc ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — CADASTRAR / EDITAR MÁQUINA PRÓPRIA
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog open={maqModal} onClose={() => setMaqModal(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editMaqId ? 'Editar Máquina' : 'Cadastrar Máquina Própria'}</DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2}>
            {modelos.length === 0 && (
              <Alert severity="warning">
                Nenhum modelo PTA cadastrado. Acesse a aba "Modelos PTA" primeiro.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField key={`tag-${editMaqId ?? 'new'}`} label="Tag / ID" defaultValue={maqForm.tag}
                onBlur={e => setMaqForm(f => ({ ...f, tag: e.target.value }))} sx={{ width: 130 }} placeholder="Ex: PTA-001" />
              <Autocomplete
                sx={{ flex: 1 }}
                options={modelos}
                getOptionLabel={m => modeloLabel(m)}
                value={modelos.find(m => m.id === maqForm.modelo_pta_id) || null}
                onChange={(_, v) => setMaqForm(f => ({ ...f, modelo_pta_id: v?.id || null, modelo: v?.nome || '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={params => (
                  <TextField {...params} label="Modelo PTA *" placeholder="Selecione..."
                    error={!maqForm.modelo_pta_id}
                    helperText={!maqForm.modelo_pta_id ? 'Selecione um modelo do catálogo' : ''}
                  />
                )}
              />
            </Box>
            <TextField key={`desc-${editMaqId ?? 'new'}`} label="Descrição" defaultValue={maqForm.descricao}
              onBlur={e => setMaqForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={2} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select value={maqForm.status} label="Status" onChange={e => setMaqForm(f => ({ ...f, status: e.target.value }))}>
                  {STATUSES_MAQ.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 180 }}>
                <InputLabel>Cliente (onde está)</InputLabel>
                <Select value={maqForm.cliente_id} label="Cliente (onde está)" onChange={e => setMaqForm(f => ({ ...f, cliente_id: e.target.value }))}>
                  <MenuItem value="">Sem cliente</MenuItem>
                  {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField key={`loc-${editMaqId ?? 'new'}`} label="Localização" defaultValue={maqForm.localizacao}
                onBlur={e => setMaqForm(f => ({ ...f, localizacao: e.target.value }))} sx={{ flex: 1 }} placeholder="Ex: Depósito Central" />
              <TextField key={`aq-${editMaqId ?? 'new'}`} label="Data de Aquisição" type="date" defaultValue={maqForm.data_aquisicao}
                onBlur={e => setMaqForm(f => ({ ...f, data_aquisicao: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 180 }} />
            </Box>
            <TextField key={`obsm-${editMaqId ?? 'new'}`} label="Observações" defaultValue={maqForm.observacoes}
              onBlur={e => setMaqForm(f => ({ ...f, observacoes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 0.5 }}>
          {maqCountdown !== null && (
            <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>
              {maqAutoSaveMsg ? `${maqAutoSaveMsg} · Próximo em ${maqCountdown}s` : `Salvando em ${maqCountdown}s...`}
            </Typography>
          )}
          <Button onClick={() => setMaqModal(false)}>Cancelar</Button>
          <Button variant="outlined" color="warning" onClick={() => handleSaveMaq(true)} disabled={savingMaq}>
            Salvar Rascunho
          </Button>
          <Button variant="contained" onClick={() => handleSaveMaq(false)} disabled={savingMaq} size="large">
            {savingMaq ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox de foto */}
      <Dialog open={!!fotoPopup} onClose={() => setFotoPopup('')} maxWidth={false} PaperProps={{
        sx: { bgcolor: 'transparent', boxShadow: 'none', m: 1 },
        onClick: () => setFotoPopup(''),
      }}>
        <Box
          component="img"
          src={fotoPopup}
          alt="Foto equipamento"
          sx={{
            maxWidth: '90vw', maxHeight: '90vh',
            objectFit: 'contain', borderRadius: 2,
            display: 'block', cursor: 'zoom-out',
          }}
        />
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
